import { Button, InlineNotification, Tag } from '@carbon/react';
import { ColumnDef } from '@tanstack/react-table';
import { FC, useContext, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useStore } from '@tanstack/react-store';

import { EntitySearchTable } from 'components/entity-search-table';
import type { EntityWithAccountsResponse } from 'types/api-responses';

import { AfeWizardCtx } from '../context';
import { afeEntitySearchAtom } from '../atoms';
import styles from './entity-step.module.scss';

/**
 * Step 2 — Entity selection. Users search entities and add them as
 * Senders or Receivers via per-row action buttons. Selected entities
 * appear as dismissible tags above the search. Step is optional —
 * no validation gating.
 * @return {JSX.Element} The entity selection step content
 */
const EntityStep: FC = () => {
  const { form } = useContext(AfeWizardCtx)!;
  const entities = useStore(form.store, (s) => s.values.entities);
  const searchResults = useAtomValue(afeEntitySearchAtom.searchAtom);

  const senders = entities.filter((e) => e.role === 'sender');
  const receivers = entities.filter((e) => e.role === 'receiver');

  /**
   * Add a selected entity to the form with the given role.
   * Prevents duplicate entity+role combinations.
   * @param {EntityWithAccountsResponse} entity - Entity from search results
   * @param {'sender' | 'receiver'} role - Role to assign
   */
  const handleAddEntity = (
    entity: EntityWithAccountsResponse,
    role: 'sender' | 'receiver'
  ) => {
    const existing = entities.find((e) => e.entityId === entity.id);
    if (existing?.role === role) return;

    // Remove existing entry (if switching role) and add with new role
    const filtered = entities.filter((e) => e.entityId !== entity.id);
    form.setFieldValue('entities', [
      ...filtered,
      {
        entityId: entity.id,
        name: entity.name,
        href: entity.href,
        address: entity.address,
        entityType: entity.importType,
        role,
      },
    ]);
  };

  /**
   * Remove an entity from the form by its entityId and role.
   * @param {number} entityId - Entity ID to remove
   * @param {string} role - Role of the entity to remove
   */
  const handleRemoveEntity = (entityId: number, role: string) => {
    form.setFieldValue(
      'entities',
      entities.filter((e) => !(e.entityId === entityId && e.role === role))
    );
  };

  /** Actions column with per-row + Receiver / + Sender buttons */
  const extraColumns: ColumnDef<EntityWithAccountsResponse, unknown>[] =
    useMemo(
      () => [
        {
          id: 'actions',
          header: '',
          cell: ({ row }) => {
            // Don't render actions on sub-rows
            if ('isSubRow' in row.original && row.original.isSubRow)
              return null;
            const currentRole = entities.find(
              (e) => e.entityId === row.original.id
            )?.role;
            return (
              <div className={styles.actionButtons}>
                <Button
                  kind="ghost"
                  size="sm"
                  disabled={currentRole === 'receiver'}
                  onClick={() => handleAddEntity(row.original, 'receiver')}
                >
                  + Receiver
                </Button>
                <Button
                  kind="ghost"
                  size="sm"
                  disabled={currentRole === 'sender'}
                  onClick={() => handleAddEntity(row.original, 'sender')}
                >
                  + Sender
                </Button>
              </div>
            );
          },
        },
      ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [entities]
    );

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>Select Entity</h2>
      <p className={styles.subtitle}>
        Search for entities and add them as Senders or Receivers.
      </p>

      {entities.length > 0 && (
        <div className={styles.selectionsBar}>
          {senders.length > 0 && (
            <div className={styles.roleGroup}>
              <span className={styles.roleLabel}>Senders</span>
              {senders.map((e) => (
                <Tag
                  key={`sender-${e.entityId}`}
                  type="gray"
                  filter
                  onClose={() => handleRemoveEntity(e.entityId, 'sender')}
                >
                  {e.name}
                </Tag>
              ))}
            </div>
          )}
          {receivers.length > 0 && (
            <div className={styles.roleGroup}>
              <span className={styles.roleLabel}>Receivers</span>
              {receivers.map((e) => (
                <Tag
                  key={`receiver-${e.entityId}`}
                  type="gray"
                  filter
                  onClose={() => handleRemoveEntity(e.entityId, 'receiver')}
                >
                  {e.name}
                </Tag>
              ))}
            </div>
          )}
        </div>
      )}

      {searchResults.error && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Entity search failed"
          subtitle="Please try again."
          hideCloseButton
        />
      )}

      <EntitySearchTable
        searchAtom={afeEntitySearchAtom}
        extraColumns={extraColumns}
      />
    </div>
  );
};

export { EntityStep };
